import React, { useState } from 'react';
import { Sparkles, Loader2, Check, X, ArrowRight, Gift } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * iter 98 - In-portal promo code redemption.
 *
 * Logged-in users hit this page when they receive a code post-signup.
 * Reuses the same `/api/promo/redeem` endpoint used by signup; the
 * backend handles all guards (invalid, already-redeemed, already
 * higher-tier).
 */
const PortalRedeem = () => {
  const { user, setUser } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/promo/redeem`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await r.json();
      setResult(data);
      // On success, optimistically update the user role in context so the
      // sidebar unlocks immediately (no full reload).
      if (data?.success && data?.granted_role && user) {
        setUser({ ...user, role: data.granted_role });
      }
    } catch (err) {
      setResult({ success: false, message: 'Network error - please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout activeTab={null}>
      <div className="min-h-screen bg-[#F9F6EF] py-12 px-4 sm:px-8" data-testid="portal-redeem-page">
        <div className="max-w-xl mx-auto">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-3">
            Promo
          </p>
          <h1 className="font-display italic text-4xl sm:text-5xl text-[#0A1F2E] leading-tight mb-3">
            Redeem a code.
          </h1>
          <p className="text-[#0A1F2E]/65 text-base mb-8 max-w-md">
            Have a Mundalo promo code? Enter it below and we'll unlock the matching tier on this account.
          </p>

          <div className="bg-white border border-[#EFE6CF] rounded-2xl shadow-sm p-6 sm:p-8">
            {result?.success ? (
              <div className="text-center" data-testid="redeem-success">
                <div className="w-14 h-14 rounded-full bg-[#2CA8A0]/15 border border-[#2CA8A0]/40 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-[#2CA8A0]" />
                </div>
                <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-2">
                  You're in.
                </h2>
                <p className="text-sm text-[#0A1F2E]/75 leading-relaxed max-w-sm mx-auto mb-5">
                  {result.message}
                </p>
                <div className="bg-[#FCF8EE] border border-[#EFE6CF] rounded-xl px-4 py-3 inline-flex items-center gap-3 mb-6">
                  <Gift className="w-4 h-4 text-[#1A5C6E]" />
                  <span className="font-mono text-xs uppercase tracking-wide text-[#0A1F2E]">
                    {result.code} · {result.duration_days} days · {result.granted_role}
                  </span>
                </div>
                <div>
                  <a
                    href="/portal/dashboard"
                    className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition"
                    data-testid="redeem-go-dashboard"
                  >
                    Go to your dashboard <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="redeem-form">
                <div>
                  <label htmlFor="redeem-code" className="block text-sm font-medium text-[#0A1F2E] mb-2">
                    Promo code
                  </label>
                  <input
                    id="redeem-code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="WELCOME30"
                    autoComplete="off"
                    autoFocus
                    className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] placeholder:text-[#0A1F2E]/35 px-4 py-3 rounded-lg focus:border-[#2CA8A0] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/25 font-mono tracking-wider uppercase transition"
                    data-testid="redeem-code-input"
                  />
                </div>

                {result && !result.success && (
                  <div
                    className="flex items-start gap-2 bg-[#C97A4A]/10 border border-[#C97A4A]/40 rounded-lg px-3 py-2.5"
                    data-testid="redeem-error"
                  >
                    <X className="w-4 h-4 text-[#C97A4A] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#0A1F2E]">{result.message}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !code.trim()}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] disabled:opacity-50 text-white font-mono uppercase text-[12px] tracking-[0.15em] font-semibold py-3.5 rounded-full transition"
                  data-testid="redeem-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {submitting ? 'Redeeming…' : 'Redeem code'}
                </button>

                <p className="text-xs text-[#0A1F2E]/55 text-center pt-1">
                  Codes are case-insensitive. One redemption per code, per email.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalRedeem;
